/* What a conversation may have attached, and the checks the composer makes
 * before uploading. The server decides (IssaInsightsGetFileRules) and checks
 * again; these are only so the user hears about a wrong file before waiting
 * for an upload. */

const MIB = 1024 * 1024;

/** One kind of file the user may attach. */
export interface IssaFileType {
  /** ".csv" - what the file name must end in, any case. */
  extension: string;
  mediaType: string;
  maxBytes: number;
}

export interface IssaFileRules {
  types: IssaFileType[];
  maxFiles: number;
  /** What one user may attach in 24 hours, across every conversation. */
  dailyBytes: number;
  /** False while impersonating: the server refuses the upload. */
  uploadAllowed: boolean;
  /** Whether answers may hand back files (an Excel workbook, a CSV). */
  outputEnabled: boolean;
}

/** IssaInsightsGetFileRules as the server sends it. False, zero and empty
 *  values are left out of the JSON, so every field may be missing. */
export interface IssaFileRulesResponse {
  Types?: Array<{ Extension?: string; MediaType?: string; MaxBytes?: number }> | null;
  MaxFilesPerConversation?: number;
  DailyUploadBytes?: number;
  UploadAllowed?: boolean;
  OutputEnabled?: boolean;
}

/** Media types by extension, for the files the assistant reads or hands back. */
const MEDIA_TYPES: Record<string, string> = {
  ".txt": "text/plain",
  ".csv": "text/csv",
  ".tsv": "text/tab-separated-values",
  ".json": "application/json",
  ".md": "text/markdown",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".webp": "image/webp",
};

/** Pictures: shown to the model rather than read in code, and offered by the server only for a
 *  conversation whose model can see them. */
const PICTURES = new Set([".png", ".jpg", ".jpeg", ".webp"]);

/** How a kind of file is named to the user. */
const LABELS: Record<string, string> = {
  ".txt": "Text",
  ".csv": "CSV",
  ".tsv": "TSV",
  ".json": "JSON",
  ".md": "Markdown",
  ".xlsx": "Excel",
  ".png": "Picture",
  ".jpg": "Picture",
  ".jpeg": "Picture",
  ".webp": "Picture",
};

/** The server's rules as they were when this was written - used until its own
 *  arrive, and if they never do. No pictures: only the server knows whether the
 *  conversation's model can see them. */
export const DEFAULT_ISSA_FILE_RULES: IssaFileRules = {
  types: [
    { extension: ".txt", mediaType: MEDIA_TYPES[".txt"]!, maxBytes: 5 * MIB },
    { extension: ".csv", mediaType: MEDIA_TYPES[".csv"]!, maxBytes: 5 * MIB },
    { extension: ".tsv", mediaType: MEDIA_TYPES[".tsv"]!, maxBytes: 5 * MIB },
    { extension: ".json", mediaType: MEDIA_TYPES[".json"]!, maxBytes: 5 * MIB },
    { extension: ".xlsx", mediaType: MEDIA_TYPES[".xlsx"]!, maxBytes: 10 * MIB },
  ],
  maxFiles: 3,
  dailyBytes: 50 * MIB,
  uploadAllowed: true,
  outputEnabled: true,
};

/** The server's answer as rules, filling in what its JSON leaves out. */
export function toIssaFileRules(response: IssaFileRulesResponse | null | undefined): IssaFileRules {
  if (!response) return DEFAULT_ISSA_FILE_RULES;
  const types = (response.Types ?? [])
    .filter(type => typeof type.Extension === "string" && /^\.[a-z0-9]+$/i.test(type.Extension) && (type.MaxBytes ?? 0) > 0)
    .map(type => ({
      extension: type.Extension!.toLowerCase(),
      mediaType: type.MediaType || MEDIA_TYPES[type.Extension!.toLowerCase()] || "application/octet-stream",
      maxBytes: type.MaxBytes!,
    }));
  return {
    types,
    maxFiles: response.MaxFilesPerConversation || DEFAULT_ISSA_FILE_RULES.maxFiles,
    dailyBytes: response.DailyUploadBytes || DEFAULT_ISSA_FILE_RULES.dailyBytes,
    uploadAllowed: response.UploadAllowed === true && types.length > 0,
    outputEnabled: response.OutputEnabled === true,
  };
}

function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  return dot > 0 ? fileName.slice(dot).toLowerCase() : "";
}

/** The kind of a file by its name, or null when it may not be attached. */
export function issaFileTypeFor(fileName: string, rules: IssaFileRules): IssaFileType | null {
  const extension = extensionOf(fileName);
  return rules.types.find(type => type.extension === extension) ?? null;
}

/** The media type an upload is labelled with. The server goes by the extension
 *  either way; this keeps the request honest. */
export function issaMediaTypeFor(fileName: string): string {
  return MEDIA_TYPES[extensionOf(fileName)] ?? "application/octet-stream";
}

/** "Excel", "CSV" - the kind of a file by its name or media type, for a label. */
export function issaFileKindLabel(fileName: string, mediaType?: string | null): string {
  const byName = LABELS[extensionOf(fileName)];
  if (byName) return byName;
  const extension = Object.keys(MEDIA_TYPES).find(key => MEDIA_TYPES[key] === mediaType);
  return (extension && LABELS[extension]) || "File";
}

/** A picture, by its name. */
export function isIssaPicture(fileName: string): boolean {
  return PICTURES.has(extensionOf(fileName));
}

/** "Text, CSV, TSV, JSON, Excel or Picture" */
export function issaFileTypesLabel(rules: IssaFileRules): string {
  const names = [...new Set(rules.types.map(type => LABELS[type.extension] ?? type.extension.slice(1).toUpperCase()))];
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
}

/** The file input's accept attribute: each extension and its media type. */
export function issaAcceptAttribute(rules: IssaFileRules): string {
  return rules.types.flatMap(type => [type.extension, type.mediaType]).join(",");
}

/** A brand's own assistant model stands in for the Analyst one, so it carries
 *  the same file support. */
export function modelSupportsAttachments(modelConfigId: string, assistantModelId?: string | null): boolean {
  if (assistantModelId && modelConfigId === assistantModelId) return true;
  return modelConfigId === "default" || modelConfigId === "test" || modelConfigId === "group";
}

/** The name as the server will take it: printable ASCII, no path, at most 128
 *  characters, and the extension kept whenever it is one of `extensions`. */
export function formatIssaFilename(
  filename: string,
  extensions: readonly string[] = DEFAULT_ISSA_FILE_RULES.types.map(type => type.extension),
): string {
  const lastDot = filename.lastIndexOf(".");
  const candidate = lastDot > 0 ? filename.slice(lastDot) : "";
  const extension = extensions.includes(candidate.toLowerCase()) ? candidate : "";
  const basename = extension ? filename.slice(0, -extension.length) : filename;
  const asciiBasename = basename
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^\x20-\x7e]/g, "-")
    .replace(/[\\/]/g, "-")
    .replace(/\s+/g, " ")
    .replace(/-+/g, "-")
    .trim()
    .replace(/^[.\- ]+|[.\- ]+$/g, "") || "file";
  return `${asciiBasename.slice(0, 128 - extension.length)}${extension}`;
}

export function validateIssaFile(file: Pick<File, "name" | "size">, rules: IssaFileRules): string | null {
  if (file.name.length < 1 || file.name.length > 128) return "Filename must be between 1 and 128 characters";
  if (!/^[\x20-\x7e]+$/.test(file.name) || file.name.includes("/") || file.name.includes("\\")) {
    return "Filename must contain printable ASCII characters only";
  }
  const type = issaFileTypeFor(file.name, rules);
  if (!type) return `Attach a ${issaFileTypesLabel(rules)} file`;
  if (file.size < 1) return "The file is empty";
  if (file.size > type.maxBytes) {
    return `${issaFileKindLabel(file.name)} files can be at most ${formatIssaFileSize(type.maxBytes)}`;
  }
  return null;
}

const PASTED_EXTENSIONS: Record<string, string> = {
  "image/png": ".png",
  "image/jpeg": ".jpg",
  "image/webp": ".webp",
};

/** A name for a picture pasted from the clipboard. The browser calls every one "image.png", so
 *  a screenshot gets the time it was pasted instead: "Screenshot 2026-10-16 14.05.33.png";
 *  `index` tells apart several pasted at once. A file with a name of its own keeps it. */
export function pastedFileName(file: Pick<File, "name" | "type">, now: Date, index = 0): string {
  const extension = PASTED_EXTENSIONS[file.type] ?? (extensionOf(file.name) || ".png");
  if (file.name && !/^image\.(png|jpe?g|webp)$/i.test(file.name)) return file.name;
  const two = (n: number) => String(n).padStart(2, "0");
  const stamp = `${now.getFullYear()}-${two(now.getMonth() + 1)}-${two(now.getDate())} ` +
    `${two(now.getHours())}.${two(now.getMinutes())}.${two(now.getSeconds())}`;
  return `Screenshot ${stamp}${index > 0 ? ` (${index + 1})` : ""}${extension}`;
}

export function formatIssaFileSize(bytes: number): string {
  if (bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < MIB) return `${Math.round(bytes / 1024)} KiB`;
  const mib = bytes / MIB;
  return Number.isInteger(mib) ? `${mib} MiB` : `${mib.toFixed(1)} MiB`;
}

/** What files dragged over the chat would do: be attached, or not now and why - the same cases
 *  that disable the attach button (an answer on its way, the conversation's limit reached). */
export interface IssaDropState {
  accepts: boolean;
  title: string;
  hint: string;
}

export function issaDropState(args: {
  attached: number;
  maxFiles: number;
  busy: boolean;
  assistant: string;
  kinds: string;
}): IssaDropState {
  const { attached, maxFiles, busy, assistant, kinds } = args;
  if (busy) return { accepts: false, title: "Wait for the answer", hint: `Files can be attached once ${assistant} has answered.` };
  const room = maxFiles - attached;
  if (room <= 0) {
    return { accepts: false, title: `${maxFiles} ${maxFiles === 1 ? "file is" : "files are"} attached already`, hint: "Remove one to attach another." };
  }
  return { accepts: true, title: "Drop to attach", hint: `${kinds} · up to ${room} more ${room === 1 ? "file" : "files"}` };
}
